// navigation/AppWrapper.tsx
import React, { FC, ReactNode, Suspense, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import AppLoader from "components/common/AppLoader/AppLoader";
import AppNotification from "components/common/AppNotification/AppNotification";
import { useAppSelector } from "core/helpers";
import { useAuth } from "hooks/core";
import { ErrorBoundary } from "react-error-boundary";
import ErrorBoundaryFallback from "components/common/Error/ErrorBoundaryFallback";
import { useDispatch } from "react-redux";
import {
  setFirstDayOfTheWeek,
  setMileageBillableRate,
  setSerialNumberFieldLabel,
  setServerTimezone,
  setSettingsInitialized,
  setTimezones,
} from "store/slices/appSlicer";
import useAppSettings from "hooks/core/useAppSettings";
import sortTimezonesByUSAFirst from "core/utils/timezones";
import { tz } from "moment-timezone";
import { findTimezoneMatch } from "core/utils";
import useSubWorkOrderItem from "hooks/admin/WO/useSubWorkOrderItem";
import useLocalStorage from "hooks/common/useLocalStorage";
import { useLSActions } from "../hooks/core";
import { usePreferences } from "../hooks/core/usePreferences";
import { FirstDayOfTheWeek } from "../core/types/enums/FirstDayOfTheWeek";
import { GlobalAdminRole } from "../components/tenants/helpers/isGlobalAdmin";
import type { ITimeZones } from "../core/types/entities/IItem";

type TProps = { children: ReactNode };

const AppWrapper: FC<TProps> = ({ children }) => {
  const location = useLocation();
  const { getTimeZones } = useAppSettings();
  const { fetchPreferences } = usePreferences();
  const { appInitialized, currentUser } = useAppSelector((s) => s.app);
  const { checkIfLoggedInUserIsValid } = useAuth();
  const { checkTimersNew } = useSubWorkOrderItem();
  const { asyncStorageGetItem } = useLocalStorage();
  const dispatch = useDispatch();
  const { setToLs, getFromLs } = useLSActions();

  const initAppSettings = async () => {
    if (!appInitialized || !currentUser) return;

    // Drop the previous tenant's rate up front: a failed load must read as
    // unknown rather than silently keeping a value from another tenant.
    dispatch(setMileageBillableRate(undefined));

    try {
      const [data, appSettings] = await Promise.all([getTimeZones(), fetchPreferences()]);

      dispatch(setSerialNumberFieldLabel(appSettings?.serialNumberFieldLabel || "Serial Number"));
      // Left unknown when the fetch failed, so a network error is not mistaken
      // for a tenant that has no mileage rate configured.
      if (appSettings) {
        dispatch(setMileageBillableRate(appSettings.mileageBillableRate ?? null));
      }

      let serverTimeZone = data.find((t) => t.id === appSettings?.serverTimeZone);
      if (!serverTimeZone) {
        serverTimeZone = data.find((t) => t.displayName === appSettings?.serverTimeZone);
      }

      if (!serverTimeZone) {
        // API succeeded but preference ID matched nothing — use last cached value
        const cached = await getFromLs<ITimeZones>("serverTimezone");
        if (cached) serverTimeZone = cached;
      }

      if (serverTimeZone) {
        const momentTimeZone = findTimezoneMatch(serverTimeZone, tz.names());
        dispatch(
          setFirstDayOfTheWeek(
            (appSettings?.firstDayOfTheWeek as FirstDayOfTheWeek) || FirstDayOfTheWeek.Monday,
          ),
        );
        dispatch(setServerTimezone({ ...serverTimeZone, momentTzId: momentTimeZone }));
        if (momentTimeZone) tz.setDefault(momentTimeZone);
        dispatch(setTimezones(sortTimezonesByUSAFirst(data)));
        setToLs("serverTimezone", JSON.stringify(serverTimeZone));
        setToLs("timezones", JSON.stringify(data));
      }

      dispatch(setSettingsInitialized(true));
    } catch {
      // API call(s) failed — apply cached timezone so the app doesn't show UTC
      const cached = await getFromLs<ITimeZones>("serverTimezone");
      if (cached) {
        const momentTimeZone = findTimezoneMatch(cached, tz.names());
        dispatch(setServerTimezone({ ...cached, momentTzId: momentTimeZone }));
        if (momentTimeZone) tz.setDefault(momentTimeZone);
      }
      // Always unblock the render gate — infinite loader is worse than UTC fallback
      dispatch(setSettingsInitialized(true));
    }
  };

  useEffect(() => {
    checkIfLoggedInUserIsValid();
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    if (currentUser.roles?.includes(GlobalAdminRole)) {
      dispatch(setSettingsInitialized(true));
      return;
    }

    dispatch(setSettingsInitialized(false));
    initAppSettings();
  }, [currentUser?.id, appInitialized]);

  useEffect(() => {
    const checkAndHandleTimers = async () => {
      const token = await asyncStorageGetItem("token");
      if (token) checkTimersNew();
    };
    if (appInitialized && currentUser && !currentUser?.roles?.includes(GlobalAdminRole)) {
      checkAndHandleTimers();
    }
  }, [checkTimersNew, appInitialized, currentUser]);

  // Any change in these keys will reset the boundary.
  const resetKeys = useMemo(
    () => [location.pathname, appInitialized, currentUser?.id],
    [location.pathname, appInitialized, currentUser?.id],
  );

  return (
    <>
      <Suspense fallback={<AppLoader />}>
        <ErrorBoundary FallbackComponent={ErrorBoundaryFallback} resetKeys={resetKeys}>
          {appInitialized ? children : null}
        </ErrorBoundary>
      </Suspense>
      <AppNotification />
    </>
  );
};

export default AppWrapper;
